import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { BaseSubsectionAdapter } from 'components/Assessments/adapters/BaseSubsectionAdapter'
import { AdapterOptions, FlatAnswerMutation } from 'components/Assessments/adapters/types'
import { getAnswerValuesFromItems } from 'components/Assessments/helpers'
import { SocAssessmentQuestionnaireItem } from 'components/Assessments/SocAssessment/types/questionnaire'
import { SocFormData } from 'components/Assessments/SocAssessment/types/SocFormData'
import { SocFormField } from 'components/Assessments/SocAssessment/types/SocFormField'

/**
 * History subsection adapter
 * Handles 60-day history tracking and medical/surgical history (16 fields)
 */
export class HistoryAdapter extends BaseSubsectionAdapter<SocFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<SocFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // 60-day history fields
    const history60DayEr = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_HISTORY_60_DAY_ER
    )?.at(0)

    const historyErNotes = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_HISTORY_ER_NOTES
    )?.at(0)

    const history60DayHospital = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_HISTORY_60_DAY_HOSPITAL
    )?.at(0)

    const historyHospitalNotes = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_HISTORY_HOSPITAL_NOTES
    )?.at(0)

    const history60DayInjuries = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_HISTORY_60_DAY_INJURIES
    )?.at(0)

    const historyInjuryNotes = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_HISTORY_INJURY_NOTES
    )?.at(0)

    const history60DayFalls = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_HISTORY_60_DAY_FALLS
    )?.at(0)

    const historyFallNotes = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_HISTORY_FALL_NOTES
    )?.at(0)

    const history60DayInfections = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_HISTORY_60_DAY_INFECTIONS
    )?.at(0)

    const historyInfectionNotes = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_HISTORY_INFECTION_NOTES
    )?.at(0)

    const history60DayMd = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_HISTORY_60_DAY_MD
    )?.at(0)

    const historyMdNotes = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_HISTORY_MD_NOTES
    )?.at(0)

    // Other history fields
    const functionalLimitations = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_HISTORY_FUNCTIONAL_LIMITATIONS
    )

    const activitiesPermitted = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_HISTORY_ACTIVITIES_PERMITTED
    )

    const medicalHistory = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_HISTORY_MEDICAL_HISTORY
    )?.at(0)

    const surgicalHistory = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_HISTORY_SURGICAL_HISTORY
    )?.at(0)

    return {
      [SocFormField.HISTORY_60_DAY_ER]: history60DayEr,
      [SocFormField.HISTORY_ER_NOTES]: historyErNotes,
      [SocFormField.HISTORY_60_DAY_HOSPITAL]: history60DayHospital,
      [SocFormField.HISTORY_HOSPITAL_NOTES]: historyHospitalNotes,
      [SocFormField.HISTORY_60_DAY_INJURIES]: history60DayInjuries,
      [SocFormField.HISTORY_INJURY_NOTES]: historyInjuryNotes,
      [SocFormField.HISTORY_60_DAY_FALLS]: history60DayFalls,
      [SocFormField.HISTORY_FALL_NOTES]: historyFallNotes,
      [SocFormField.HISTORY_60_DAY_INFECTIONS]: history60DayInfections,
      [SocFormField.HISTORY_INFECTION_NOTES]: historyInfectionNotes,
      [SocFormField.HISTORY_60_DAY_MD]: history60DayMd,
      [SocFormField.HISTORY_MD_NOTES]: historyMdNotes,
      [SocFormField.HISTORY_FUNCTIONAL_LIMITATIONS]: functionalLimitations,
      [SocFormField.HISTORY_ACTIVITIES_PERMITTED]: activitiesPermitted,
      [SocFormField.HISTORY_MEDICAL_HISTORY]: medicalHistory,
      [SocFormField.HISTORY_SURGICAL_HISTORY]: surgicalHistory
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = [
      {
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_HISTORY_60_DAY_ER,
        value: this.getFormDataField(SocFormField.HISTORY_60_DAY_ER)
      },
      {
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_HISTORY_60_DAY_HOSPITAL,
        value: this.getFormDataField(SocFormField.HISTORY_60_DAY_HOSPITAL)
      },
      {
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_HISTORY_60_DAY_INJURIES,
        value: this.getFormDataField(SocFormField.HISTORY_60_DAY_INJURIES)
      },
      {
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_HISTORY_60_DAY_FALLS,
        value: this.getFormDataField(SocFormField.HISTORY_60_DAY_FALLS)
      },
      {
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_HISTORY_60_DAY_INFECTIONS,
        value: this.getFormDataField(SocFormField.HISTORY_60_DAY_INFECTIONS)
      },
      {
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_HISTORY_60_DAY_MD,
        value: this.getFormDataField(SocFormField.HISTORY_60_DAY_MD)
      },
      {
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_HISTORY_FUNCTIONAL_LIMITATIONS,
        value: this.getFormDataField(SocFormField.HISTORY_FUNCTIONAL_LIMITATIONS)
      },
      {
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_HISTORY_ACTIVITIES_PERMITTED,
        value: this.getFormDataField(SocFormField.HISTORY_ACTIVITIES_PERMITTED)
      },
      {
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_HISTORY_MEDICAL_HISTORY,
        value: this.getFormDataField(SocFormField.HISTORY_MEDICAL_HISTORY)
      },
      {
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_HISTORY_SURGICAL_HISTORY,
        value: this.getFormDataField(SocFormField.HISTORY_SURGICAL_HISTORY)
      }
    ]

    // Conditionally add notes fields if they exist (they are conditionally rendered)
    if (this.formDataFieldExists(SocFormField.HISTORY_ER_NOTES)) {
      mutations.push({
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_HISTORY_ER_NOTES,
        value: this.getFormDataField(SocFormField.HISTORY_ER_NOTES)
      })
    }

    if (this.formDataFieldExists(SocFormField.HISTORY_HOSPITAL_NOTES)) {
      mutations.push({
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_HISTORY_HOSPITAL_NOTES,
        value: this.getFormDataField(SocFormField.HISTORY_HOSPITAL_NOTES)
      })
    }

    if (this.formDataFieldExists(SocFormField.HISTORY_INJURY_NOTES)) {
      mutations.push({
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_HISTORY_INJURY_NOTES,
        value: this.getFormDataField(SocFormField.HISTORY_INJURY_NOTES)
      })
    }

    if (this.formDataFieldExists(SocFormField.HISTORY_FALL_NOTES)) {
      mutations.push({
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_HISTORY_FALL_NOTES,
        value: this.getFormDataField(SocFormField.HISTORY_FALL_NOTES)
      })
    }

    if (this.formDataFieldExists(SocFormField.HISTORY_INFECTION_NOTES)) {
      mutations.push({
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_HISTORY_INFECTION_NOTES,
        value: this.getFormDataField(SocFormField.HISTORY_INFECTION_NOTES)
      })
    }

    if (this.formDataFieldExists(SocFormField.HISTORY_MD_NOTES)) {
      mutations.push({
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_HISTORY_MD_NOTES,
        value: this.getFormDataField(SocFormField.HISTORY_MD_NOTES)
      })
    }

    return mutations
  }
}
